import React, { useState } from "react";
import { ChevronUp, ChevronDown, ArrowUpDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// Local sort + search state for a table/list
export const useTableControls = (defaultSortField = null, defaultSearchField = "") => {
  const [sortField, setSortField] = useState(defaultSortField);
  const [sortDir, setSortDir] = useState("asc");
  const [searchField, setSearchField] = useState(defaultSearchField);
  const [searchText, setSearchText] = useState("");

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  return {
    sortField, sortDir, setSortField, setSortDir, handleSort,
    searchField, setSearchField, searchText, setSearchText,
  };
};

// Clickable sortable table header cell
export const SortableTh = ({ field, label, sortField, sortDir, onSort, className = "", align = "left" }) => (
  <th
    className={`py-3 px-4 text-${align} text-xs font-bold uppercase text-zinc-300 cursor-pointer select-none hover:text-crew-yellow ${className}`}
    onClick={() => onSort(field)}
    data-testid={`sort-${field}`}
  >
    <span className="inline-flex items-center gap-1">
      {label}
      {sortField === field ? (
        sortDir === "asc" ? <ChevronUp size={12} /> : <ChevronDown size={12} />
      ) : (
        <ArrowUpDown size={11} className="opacity-30" />
      )}
    </span>
  </th>
);

// "Search by [field] + text box" control
export const SearchBar = ({ fields, searchField, setSearchField, searchText, setSearchText, testid = "search" }) => (
  <div className="flex gap-2 items-center flex-wrap">
    <Select value={searchField || fields[0]?.value} onValueChange={setSearchField}>
      <SelectTrigger className="bg-black border-zinc-700 text-white w-40 h-9" data-testid={`${testid}-field`}>
        <SelectValue placeholder="Search by" />
      </SelectTrigger>
      <SelectContent className="bg-zinc-900 border-zinc-700">
        {fields.map((f) => (
          <SelectItem key={f.value} value={f.value} className="text-white hover:bg-zinc-800">{f.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
    <Input
      value={searchText}
      onChange={(e) => setSearchText(e.target.value)}
      placeholder="Search..."
      className="bg-black border-zinc-700 text-white w-56 h-9"
      data-testid={`${testid}-input`}
    />
  </div>
);

// Compact "Sort by [field] [dir]" control for non-table (card) lists
export const SortSelect = ({ fields, sortField, sortDir, setSortField, setSortDir, testid = "sort" }) => (
  <div className="flex gap-2 items-center">
    <Select value={sortField || ""} onValueChange={setSortField}>
      <SelectTrigger className="bg-black border-zinc-700 text-white w-40 h-9" data-testid={`${testid}-field`}>
        <SelectValue placeholder="Sort by" />
      </SelectTrigger>
      <SelectContent className="bg-zinc-900 border-zinc-700">
        {fields.map((f) => (
          <SelectItem key={f.value} value={f.value} className="text-white hover:bg-zinc-800">{f.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
    <button
      type="button"
      onClick={() => setSortDir(sortDir === "asc" ? "desc" : "asc")}
      className="h-9 px-3 rounded-sm border border-zinc-700 text-zinc-300 hover:text-crew-yellow hover:border-crew-yellow text-sm"
      title="Toggle sort direction"
      data-testid={`${testid}-dir`}
    >
      {sortDir === "asc" ? "▲ Asc" : "▼ Desc"}
    </button>
  </div>
);

// Apply search + sort to rows using a getValue(row, field) accessor
export const applyControls = (rows, ctrl, getValue) => {
  let out = [...(rows || [])];
  const { searchField, searchText, sortField, sortDir } = ctrl;
  if (searchText && searchField) {
    const q = searchText.toLowerCase();
    out = out.filter((r) => String(getValue(r, searchField) ?? "").toLowerCase().includes(q));
  }
  if (sortField) {
    out.sort((a, b) => {
      const av = String(getValue(a, sortField) ?? "");
      const bv = String(getValue(b, sortField) ?? "");
      const cmp = av.localeCompare(bv, undefined, { numeric: true, sensitivity: "base" });
      return sortDir === "asc" ? cmp : -cmp;
    });
  }
  return out;
};
